import { parseEnhanceImageError } from "utils/parse-enhance-image-error.util";
import { getCriticalTimeoutMs } from "utils/request-timeouts";

import { emptySplitApi } from "./empty-split-api";
import { staffApi } from "./staff";
import {
  SendBirdUser,
  StaffBusyInterval,
  UserChangePassword,
  UserDetails,
  UserPhotoInput,
  UserPreferences,
  UserResponse,
  UserStateLicense
} from "./types/users.type";

export const usersApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<UserResponse, number>({
      query: (id) => `/auth/users/${id}/`,
      providesTags: ["Users"]
    }),
    getStaffBusyTimes: builder.query<
      StaffBusyInterval[],
      {
        staffId: number;
        afterMoment: string;
        beforeMoment: string;
        clinicId?: number;
        excludeAppointmentId?: number | string;
      }
    >({
      query: ({
        staffId,
        afterMoment,
        beforeMoment,
        clinicId,
        excludeAppointmentId
      }) => ({
        url: `/auth/users/${staffId}/busy-times/`,
        params: {
          after_moment: afterMoment,
          before_moment: beforeMoment,
          ...(clinicId !== undefined ? { clinic: clinicId } : {}),
          ...(excludeAppointmentId !== undefined
            ? { exclude_appointment_id: excludeAppointmentId }
            : {})
        }
      }),
      // Availability is real-time — discard cache immediately on unmount so re-opening
      // the conflict modal always fetches fresh intervals (same as getRoomBusyTimes).
      keepUnusedDataFor: 0
    }),
    getMySendBirdDetails: builder.query<SendBirdUser, void>({
      // Critical bootstrap sibling — armed with the LD bootstrap timeout
      // (undefined = off = no timeout). TIMEOUT_ERROR retries via the wrapper.
      query: () => ({
        url: "/auth/users/me/sendbird/",
        timeout: getCriticalTimeoutMs()
      }),
      providesTags: ["SendbirdUsers"]
    }),
    updateUser: builder.mutation<
      UserResponse,
      Partial<UserDetails> & { id: number }
    >({
      query: ({ id, ...data }) => ({
        url: `/auth/users/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["Users"]
    }),
    updateUserPreferences: builder.mutation<
      UserResponse,
      { id: number; user_preferences: Partial<UserPreferences> }
    >({
      query: ({ id, ...data }) => ({
        url: `/auth/users/${id}/`,
        method: "PATCH",
        body: data
      })
      // No invalidatesTags on purpose: the Redux auth slice is updated directly
      // by the caller, and these preferences are not read via the Users cache.
      // Avoids refetching the user (and resetting open settings forms) on every save.
    }),
    updateUserPhoto: builder.mutation<UserResponse, UserPhotoInput>({
      query: ({ id, formData }) => ({
        url: `/auth/users/${id}/`,
        method: "PATCH",
        body: formData
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Staff", id },
        "Users"
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            staffApi.util.updateQueryData("getStaffMember", id, (draft) => {
              if (data.signature !== undefined) {
                draft.signature = data.signature;
              }
              if (data.photo !== undefined) {
                draft.photo = data.photo;
              }
            })
          );
          dispatch(
            usersApi.util.updateQueryData("getUser", id, (draft) => {
              if (data.signature !== undefined) {
                draft.signature = data.signature;
              }
              if (data.photo !== undefined) {
                draft.photo = data.photo;
              }
            })
          );
        } catch {
          // keep stale cache on failure
        }
      }
    }),
    changePassword: builder.mutation<UserResponse, UserChangePassword>({
      query: (body) => ({
        url: `/auth/change-password/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Users"]
    }),
    updateUserStateLicenses: builder.mutation<
      UserStateLicense[],
      { id: number; state_licenses: Omit<UserStateLicense, "id">[] }
    >({
      query: ({ id, state_licenses }) => ({
        url: `/auth/users/${id}/state-licenses/`,
        method: "PATCH",
        body: state_licenses
      }),
      invalidatesTags: ["Users", "Staff"]
    }),
    enhanceSignature: builder.mutation<Blob, { formData: FormData }>({
      query: ({ formData }) => ({
        url: "/ai/signature/enhance/",
        method: "POST",
        body: formData,
        timeout: 120_000,
        // Returns the message on failure rather than throwing — RTK's validateStatus
        // then yields { error: { status, data: message } }. See the util's docstring.
        responseHandler: async (response) =>
          response.ok
            ? response.blob()
            : parseEnhanceImageError(response, "Signature optimization failed"),
        cache: "no-cache"
      })
      // No invalidatesTags — stateless, does not mutate server state
    })
  })
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useUpdateUserMutation,
  useUpdateUserPreferencesMutation,
  useUpdateUserPhotoMutation,
  useChangePasswordMutation,
  useGetMySendBirdDetailsQuery,
  useLazyGetStaffBusyTimesQuery,
  useGetStaffBusyTimesQuery,
  useUpdateUserStateLicensesMutation,
  useEnhanceSignatureMutation
} = usersApi;
